/**
 * The trainer's comment on a finished session — the one decision behind the composer.
 *
 * ── What this comment IS, on the wire and in the queue ────────────────────────────────────────
 * It is `WorkoutExecution.TrainerNote`, written with `PATCH /workout-execution/{id}`. That field
 * and no other extinguishes the `SessionAwaitingReview` attention item: the detector's query is
 * literally `w.TrainerNote == null` (`Data/Repository/AttentionQueueRepository.cs`), and
 * attention-queue **R15** says so in words — "the trainer writes the session's trainer note".
 *
 * **It is NOT the per-exercise note.** `PUT /workout-execution/{id}/exercises/{eid}/trainer-note`
 * writes `ExerciseExecutionNote.TrainerNote`, a different entity the detector never reads. Writing
 * there leaves the task standing, which is a defect a trainer would only notice by the task not
 * going away. The brief pointed at that endpoint; the code says otherwise, and the code was read.
 *
 * ── Why an empty save travels as `""` and never as `null` ─────────────────────────────────────
 * `PatchWorkoutExecutionRequest` cannot distinguish an omitted key from an explicit `null` — both
 * bind to `null` in ASP.NET and both mean "leave it alone". So clearing a comment has to travel as
 * a blank STRING, which the processor treats as a clear (text → null, timestamp → null, author
 * detached). Mobile's `useSaveSessionTrainerNote` documents the same trap; this is the same wire.
 *
 * ── And why clearing brings the task back ─────────────────────────────────────────────────────
 * The detector is live (R1): no note means the item exists again, provided the session is still
 * inside the seven-day lookback. That is correct behaviour, not a bug, and the composer says so
 * rather than letting it surprise anyone.
 */

/** The backend's own `[MaxLength(2000)]` on `PatchWorkoutExecutionRequest.TrainerNote`. */
export const SESSION_COMMENT_MAX_LENGTH = 2000;

export interface SessionCommentState {
  /** What is in the field right now. */
  readonly draft: string;
  /** What the server currently holds, so an unchanged save can be refused rather than sent. */
  readonly saved: string;
}

export interface SessionCommentDecision {
  /** Whether the save control may be pressed at all. */
  readonly canSave: boolean;
  /** Whether pressing it would DELETE the comment, which the composer warns about first. */
  readonly clears: boolean;
  /** Exactly what goes on the wire. Always a string — see the header. */
  readonly wireBody: string;
  /** True once the draft is longer than the field the server will accept. */
  readonly tooLong: boolean;
}

export function decideSessionComment(state: SessionCommentState): SessionCommentDecision {
  const wireBody = state.draft.trim();
  const saved = state.saved.trim();
  const tooLong = wireBody.length > SESSION_COMMENT_MAX_LENGTH;

  return {
    // Three refusals, and each one is a different sentence the trainer would otherwise get from the
    // server: nothing to say and nothing to clear; the same text already stored; past the limit.
    canSave: !tooLong && wireBody !== saved && (wireBody.length > 0 || saved.length > 0),
    clears: wireBody.length === 0 && saved.length > 0,
    wireBody,
    tooLong,
  };
}
